/** Cliente HTTP del módulo de Inventario (Fase 1). Espejo de app/schemas/inventario.py. */
import { apiDownload, apiPostArchivo, apiRequest } from "../../lib/api";
import type { FilaImportacion, ResultadoImportacion } from "../../components/ImportarPlanillaModal";

// ── Proveedor ──
export interface ProveedorRead {
  id: number;
  rut: string;
  razon_social: string;
  nombre_fantasia: string | null;
  giro: string | null;
  direccion: string | null;
  comuna: string | null;
  telefono: string | null;
  email: string | null;
  contacto_nombre: string | null;
  contacto_telefono: string | null;
  contacto_email: string | null;
  activo: boolean;
}

export interface ProveedorCreate {
  rut: string;
  razon_social: string;
  nombre_fantasia?: string | null;
  giro?: string | null;
  direccion?: string | null;
  comuna?: string | null;
  telefono?: string | null;
  email?: string | null;
  contacto_nombre?: string | null;
  contacto_telefono?: string | null;
  contacto_email?: string | null;
}

export type ProveedorUpdate = Partial<Omit<ProveedorCreate, "rut">> & { activo?: boolean };

// ── Bodega ──
export type TipoBodega = "Central" | "Movil";

export interface BodegaRead {
  id: number;
  nombre: string;
  tipo: TipoBodega;
  ubicacion: string | null;
  activa: boolean;
}

export interface BodegaCreate {
  nombre: string;
  tipo: TipoBodega;
  ubicacion?: string | null;
}

export type BodegaUpdate = Partial<BodegaCreate> & { activa?: boolean };

// ── Categoría ──
export interface CategoriaRead {
  id: number;
  nombre: string;
  descripcion: string | null;
  requiere_sic_siempre: boolean;
}

export interface CategoriaCreate {
  nombre: string;
  descripcion?: string | null;
  requiere_sic_siempre?: boolean;
}

export type CategoriaUpdate = Partial<CategoriaCreate>;

// ── Insumo ──
// Campos Decimal (stock_minimo/critico) pueden llegar como string desde JSON:
// tratarlos siempre con Number() al mostrar/computar.
export interface InsumoRead {
  id: number;
  nombre: string;
  categoria_id: number | null;
  codigo: string | null;
  unidad_medida: string;
  stock_minimo: number | string;
  stock_critico: number | string;
  marca: string | null;
  modelo: string | null;
  descripcion: string | null;
  ficha_tecnica_url: string | null;
  imagen_url: string | null;
  costo_promedio: number;
  ultimo_costo: number;
  activo: boolean;
}

export interface InsumoCreate {
  nombre: string;
  categoria_id?: number | null;
  codigo?: string | null;
  unidad_medida?: string;
  stock_minimo?: number;
  stock_critico?: number;
  marca?: string | null;
  modelo?: string | null;
  descripcion?: string | null;
}

export type InsumoUpdate = Partial<InsumoCreate> & { activo?: boolean };

// ── Existencias / alertas ──
export interface ExistenciaRead {
  bodega_id: number;
  bodega_nombre: string;
  insumo_id: number;
  insumo_nombre: string;
  unidad_medida: string;
  cantidad: number | string;
  ubicacion_estante: string | null;
}

export interface AlertaStock {
  insumo_id: number;
  insumo_nombre: string;
  unidad_medida: string;
  cantidad_total: number | string;
  stock_minimo: number | string;
  stock_critico: number | string;
  nivel: "critico" | "bajo";
}

// ── Movimientos ──
export type TipoMovimiento = "ENTRADA" | "SALIDA" | "TRASPASO" | "AJUSTE";

export interface DetalleMovimientoRead {
  id: number;
  insumo_id: number;
  cantidad: number | string;
  costo_unitario: number;
  costo_total: number;
}

export interface MovimientoRead {
  id: number;
  tipo: TipoMovimiento;
  motivo: string;
  fecha: string;
  bodega_origen_id: number | null;
  bodega_destino_id: number | null;
  proveedor_id: number | null;
  documento_tipo: string | null;
  numero_documento: string | null;
  ot_id: number | null;
  estado: string;
  movimiento_reversa_id: number | null;
  observacion: string | null;
  registrado_por_id: number | null;
  detalles: DetalleMovimientoRead[];
}

export interface DetalleMovimientoIn {
  insumo_id: number;
  cantidad: number;
  costo_unitario?: number;
}

export interface RegistrarMovimientoRequest {
  tipo: TipoMovimiento;
  motivo: string;
  fecha?: string | null;
  bodega_origen_id?: number | null;
  bodega_destino_id?: number | null;
  proveedor_id?: number | null;
  documento_tipo?: string | null;
  numero_documento?: string | null;
  observacion?: string | null;
  detalles: DetalleMovimientoIn[];
}

// ────────────────────────────────────────────────────────────────────────
// Endpoints
// ────────────────────────────────────────────────────────────────────────

const B = "/api/v1/inventario";

// Proveedores
export const listarProveedores = (): Promise<ProveedorRead[]> =>
  apiRequest(`${B}/proveedores`);
export const crearProveedor = (p: ProveedorCreate): Promise<ProveedorRead> =>
  apiRequest(`${B}/proveedores`, { method: "POST", body: p });
export const actualizarProveedor = (
  id: number,
  p: ProveedorUpdate,
): Promise<ProveedorRead> =>
  apiRequest(`${B}/proveedores/${id}`, { method: "PATCH", body: p });

// Bodegas
export const listarBodegas = (): Promise<BodegaRead[]> => apiRequest(`${B}/bodegas`);
export const crearBodega = (p: BodegaCreate): Promise<BodegaRead> =>
  apiRequest(`${B}/bodegas`, { method: "POST", body: p });
export const actualizarBodega = (id: number, p: BodegaUpdate): Promise<BodegaRead> =>
  apiRequest(`${B}/bodegas/${id}`, { method: "PATCH", body: p });
export const existenciasDeBodega = (id: number): Promise<ExistenciaRead[]> =>
  apiRequest(`${B}/bodegas/${id}/existencias`);
export const actualizarUbicacionExistencia = (
  bodegaId: number,
  insumoId: number,
  ubicacionEstante: string | null,
): Promise<ExistenciaRead> =>
  apiRequest(`${B}/bodegas/${bodegaId}/existencias/${insumoId}`, {
    method: "PATCH",
    body: { ubicacion_estante: ubicacionEstante },
  });

// Categorías
export const listarCategorias = (): Promise<CategoriaRead[]> =>
  apiRequest(`${B}/categorias`);
export const crearCategoria = (p: CategoriaCreate): Promise<CategoriaRead> =>
  apiRequest(`${B}/categorias`, { method: "POST", body: p });
export const actualizarCategoria = (
  id: number,
  p: CategoriaUpdate,
): Promise<CategoriaRead> =>
  apiRequest(`${B}/categorias/${id}`, { method: "PATCH", body: p });

// Insumos
export const listarInsumos = (): Promise<InsumoRead[]> => apiRequest(`${B}/insumos`);
export const crearInsumo = (p: InsumoCreate): Promise<InsumoRead> =>
  apiRequest(`${B}/insumos`, { method: "POST", body: p });
export const actualizarInsumo = (id: number, p: InsumoUpdate): Promise<InsumoRead> =>
  apiRequest(`${B}/insumos/${id}`, { method: "PATCH", body: p });
export const existenciasDeInsumo = (id: number): Promise<ExistenciaRead[]> =>
  apiRequest(`${B}/insumos/${id}/existencias`);

// Movimientos
export const listarMovimientos = (params?: {
  tipo?: TipoMovimiento;
  bodega_id?: number;
  insumo_id?: number;
  limit?: number;
}): Promise<MovimientoRead[]> => {
  const q = new URLSearchParams();
  if (params?.tipo) q.set("tipo", params.tipo);
  if (params?.bodega_id != null) q.set("bodega_id", String(params.bodega_id));
  if (params?.insumo_id != null) q.set("insumo_id", String(params.insumo_id));
  if (params?.limit != null) q.set("limit", String(params.limit));
  const qs = q.toString();
  return apiRequest(`${B}/movimientos${qs ? `?${qs}` : ""}`);
};
export const registrarMovimiento = (
  p: RegistrarMovimientoRequest,
): Promise<MovimientoRead> => apiRequest(`${B}/movimientos`, { method: "POST", body: p });
export const anularMovimiento = (
  id: number,
  motivo: string,
): Promise<MovimientoRead> =>
  apiRequest(`${B}/movimientos/${id}/anular`, { method: "POST", body: { motivo } });

// Alertas
export const listarAlertas = (): Promise<AlertaStock[]> => apiRequest(`${B}/alertas`);

// Levantamiento: importar insumos + stock inicial, exportar existencias
export interface ImportacionInsumos extends ResultadoImportacion {
  stock_cargado: number;
  stock_omitido: number;
  categorias_creadas: string[];
  bodegas_creadas: string[];
  avisos: FilaImportacion[];
}
export const importarInsumos = (archivo: File, confirmar: boolean): Promise<ImportacionInsumos> =>
  apiPostArchivo(`${B}/importar?confirmar=${confirmar}`, archivo);
export const descargarPlantillaInsumos = (): Promise<void> =>
  apiDownload(`${B}/importar/plantilla.xlsx`, "planilla_insumos.xlsx");
export const exportarExistencias = (bodegaId?: number): Promise<void> =>
  apiDownload(
    `${B}/existencias/export.xlsx${bodegaId != null ? `?bodega_id=${bodegaId}` : ""}`,
    "existencias_valorizadas.xlsx",
  );
